import { useRef, useState } from 'react';
import { importApi, type AnalyzeResult, type PreviewResult } from '../api/client.js';
import { formatAmount } from '../components/Layout.js';
import { Spinner, TableEmpty, ErrorBanner } from '../components/States.js';
import { roleCan, permHint } from '../utils/permissions.js';

// 标准字段（与后端 StandardTransaction 对齐）
const STANDARD_FIELDS: Array<{ key: string; label: string; required?: boolean }> = [
  { key: 'transactionTime', label: '交易时间', required: true },
  { key: 'amount', label: '金额', required: true },
  { key: 'transactionId', label: '流水单号' },
  { key: 'counterparty', label: '对方名称' },
  { key: 'remark', label: '备注摘要' },
  { key: 'direction', label: '收支方向' },
];

/** 问题标记：blocking = 不可入库（默认不勾选），否则仅警示 */
const ISSUE_META: Record<string, { label: string; blocking: boolean }> = {
  invalid_amount: { label: '金额无效', blocking: true },
  zero_amount: { label: '金额为零', blocking: true },
  dup_in_file: { label: '文件内重复', blocking: true },
  dup_in_db: { label: '台账已存在', blocking: true },
  missing_time: { label: '缺时间→按当前', blocking: false },
  time_unparsed: { label: '时间无法解析→按当前', blocking: false },
};

const PAGE_SIZE = 50;

export default function ImportPage() {
  const canUse = roleCan('import:use');
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState<AnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [platform, setPlatform] = useState('wechat');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 步骤 ③：逐行预览勾选
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetPreview = () => {
    setPreview(null);
    setSelected(new Set());
    setPage(0);
  };

  const analyze = async () => {
    if (!file) { setError('请先选择文件'); return; }
    setAnalyzing(true);
    setError('');
    setSuccess('');
    setAnalyzed(null);
    resetPreview();
    try {
      const res = await importApi.analyze(file);
      setAnalyzed(res);
      setMapping(res.detectedMapping);
    } catch (e: any) {
      setError(`分析失败：${e.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const doPreview = async () => {
    if (!analyzed) return;
    const missing = STANDARD_FIELDS.filter((f) => f.required && !mapping[f.key]);
    if (missing.length > 0) {
      setError(`请先映射必填字段：${missing.map((f) => f.label).join('、')}`);
      return;
    }
    setPreviewing(true);
    setError('');
    try {
      const res = await importApi.preview({ rows: analyzed.allRows, mapping });
      setPreview(res);
      setPage(0);
      // 默认为所有可导入行勾选
      setSelected(new Set(res.rows.filter((r) => r.canImport).map((r) => r.index)));
    } catch (e: any) {
      setError(`预览失败：${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const confirm = async () => {
    if (!analyzed || !preview || selected.size === 0) return;
    setPreviewing(true);
    setError('');
    try {
      // 仅提交勾选行
      const rowsToImport = analyzed.allRows.filter((_, i) => selected.has(i));
      const res = await importApi.confirm({
        platform,
        headers: analyzed.headers,
        rows: rowsToImport,
        mapping,
      });
      const c = res.classification;
      setSuccess(
        `✅ 已导入 ${res.importedCount} 条（跳过 ${res.skippedCount} 条）` +
        (c ? `；自动分类：高 ${c.byTier.high} / 中 ${c.byTier.medium} / 低 ${c.byTier.low}，重复 ${c.duplicated} 条` : '')
      );
      setAnalyzed(null);
      resetPreview();
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e: any) {
      setError(`导入失败：${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleRow = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const togglePage = (all: boolean) => {
    if (!preview) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of pageRows(preview)) {
        if (!r.canImport) continue;
        if (all) next.add(r.index);
        else next.delete(r.index);
      }
      return next;
    });
  };

  const pageRows = (p: PreviewResult) => p.rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = preview ? Math.ceil(preview.rows.length / PAGE_SIZE) : 0;

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-1">智能导入</h2>
      <p className="text-sm text-slate-500 mb-4">
        上传各平台报表（CSV / Excel），系统自动识别表头并映射为统一格式；逐行预览勾选后确认导入，异常行自动拦截
      </p>

      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}
      {success && (
        <div className="mb-4 card p-4 border-green-200 bg-green-50/60 animate-fade-in-up">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* 步骤 1：上传 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">① 选择报表文件</h3>
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
          />
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="wechat">微信</option>
            <option value="alipay">支付宝</option>
            <option value="taobao">淘宝</option>
            <option value="douyin">抖音</option>
            <option value="other">其他</option>
          </select>
          <button onClick={analyze} disabled={!file || analyzing || !canUse} title={permHint('import:use')} className="btn btn-primary">
            {analyzing ? <Spinner label="分析中…" /> : '智能分析'}
          </button>
        </div>
      </div>

      {/* 步骤 2：映射确认 */}
      {analyzed && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-1">② 确认表头映射</h3>
          <div className="text-xs text-slate-500 mb-3">
            文件「{analyzed.fileName}」共 {analyzed.totalRows} 行 ·
            识别来源：
            <span className={`ml-1 px-1.5 py-0.5 rounded ${
              analyzed.mappingSource === 'learned_template' ? 'bg-green-100 text-green-700'
                : analyzed.mappingSource === 'ai' ? 'bg-purple-100 text-purple-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {analyzed.mappingSource === 'learned_template' ? '已学习模板命中 ⚡' : analyzed.mappingSource === 'ai' ? 'ZUnicorn AI 语义识别 🤖' : '规则引擎识别'}
            </span>
            · 置信度 {(analyzed.confidence * 100).toFixed(0)}%
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {STANDARD_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-2 text-sm">
                <span className="w-24 text-slate-600">
                  {f.label}{f.required && <span className="text-red-500">*</span>}
                </span>
                <select
                  value={mapping[f.key] || ''}
                  onChange={(e) => {
                    const next = { ...mapping };
                    if (e.target.value) next[f.key] = e.target.value;
                    else delete next[f.key];
                    setMapping(next);
                    resetPreview(); // 映射变化后旧预览失效
                  }}
                  className={`flex-1 border rounded-lg px-2 py-1.5 text-sm bg-white ${
                    f.required && !mapping[f.key] ? 'border-red-300' : 'border-slate-200'
                  }`}
                >
                  <option value="">（不导入）</option>
                  {analyzed.headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {!preview && (
            <button onClick={doPreview} disabled={previewing || !canUse} title={permHint('import:use')} className="btn btn-accent">
              {previewing ? <Spinner label="校验中…" /> : '③ 逐行预览并勾选'}
            </button>
          )}
        </div>
      )}

      {/* 步骤 3：逐行预览勾选 */}
      {analyzed && preview && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">
              ③ 逐行预览
              <span className="ml-2 text-xs font-normal text-slate-400">
                共 {preview.stats.total} 行 · 可导入 {preview.stats.importable} · 异常 {preview.stats.flagged}
                · 已选 <span className="text-brand-600 font-medium">{selected.size}</span>
              </span>
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={() => togglePage(true)} className="btn btn-ghost btn-sm">全选本页</button>
              <button onClick={() => togglePage(false)} className="btn btn-ghost btn-sm">清除本页</button>
              <button
                onClick={() => { setSelected(new Set(preview.rows.filter((r) => r.canImport).map((r) => r.index))); setPage(0); }}
                className="btn btn-ghost btn-sm"
              >
                选中全部可导入
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">流水单号</th>
                  <th className="px-3 py-2 text-left">交易时间</th>
                  <th className="px-3 py-2 text-right">金额</th>
                  <th className="px-3 py-2 text-center">方向</th>
                  <th className="px-3 py-2 text-left">对方</th>
                  <th className="px-3 py-2 text-left">摘要</th>
                  <th className="px-3 py-2 text-left">问题</th>
                </tr>
              </thead>
              <tbody>
                {pageRows(preview).map((r) => {
                  const checked = selected.has(r.index);
                  return (
                    <tr
                      key={r.index}
                      className={`border-t border-slate-50 ${
                        r.canImport ? 'hover:bg-slate-50 cursor-pointer' : 'bg-red-50/40'
                      } ${checked && r.canImport ? 'bg-brand-50/40' : ''}`}
                      onClick={() => r.canImport && toggleRow(r.index)}
                    >
                      <td className="px-3 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!r.canImport}
                          onChange={() => toggleRow(r.index)}
                          onClick={(e) => e.stopPropagation()}
                          className="accent-indigo-600"
                        />
                      </td>
                      <td className="px-3 py-1.5 text-slate-400">{r.index + 1}</td>
                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">{r.transactionId || '-'}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{r.transactionTime || '-'}</td>
                      <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${r.amount === null ? 'text-red-400' : ''}`}>
                        {r.amount === null ? '（无效）' : formatAmount(r.amount)}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {r.direction === 'in'
                          ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700">收</span>
                          : r.direction === 'out'
                          ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-700">支</span>
                          : '-'}
                      </td>
                      <td className="px-3 py-1.5 max-w-[160px] truncate">{r.counterparty || '-'}</td>
                      <td className="px-3 py-1.5 max-w-[180px] truncate">{r.remark || '-'}</td>
                      <td className="px-3 py-1.5">
                        {r.issues.length === 0 ? (
                          <span className="text-slate-300">✔</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {r.issues.map((iss) => {
                              const meta = ISSUE_META[iss] || { label: iss, blocking: true };
                              return (
                                <span
                                  key={iss}
                                  className={`px-1.5 py-0.5 rounded-full text-[10px] whitespace-nowrap ${
                                    meta.blocking ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {meta.label}
                                </span>
                              );
                            })}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {pageRows(preview).length === 0 && (
                  <TableEmpty colSpan={9} icon="📭" title="没有可预览的数据行" />
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-xs text-slate-500">
              <span>第 {page + 1} / {totalPages} 页</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn btn-ghost btn-sm">上一页</button>
                <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn btn-ghost btn-sm">下一页</button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <button
              onClick={confirm}
              disabled={previewing || selected.size === 0 || !canUse}
              title={permHint('import:use')}
              className="btn btn-accent"
            >
              {previewing
                ? <Spinner label="导入中…" />
                : `确认导入所选 ${selected.size} 条${preview.stats.total - preview.stats.importable > 0 ? `（拦截 ${preview.stats.total - preview.stats.importable} 异常行）` : ''}`}
            </button>
            <button
              onClick={() => { setAnalyzed(null); resetPreview(); setError(''); }}
              className="btn btn-ghost"
            >
              取消
            </button>
            <span className="text-xs text-slate-400 ml-auto">确认过的映射会保存为模板，下次相同表头自动命中 ⚡</span>
          </div>
        </div>
      )}
    </div>
  );
}
